<script setup lang="ts">
import { ref } from 'vue'
import Popover from 'primevue/popover'

interface Props {
  dismissable?: boolean
  /**
   * `panel` — the default branded card (220px min, primary border).
   * `menu`  — compact action list: neutral border, sized to its items.
   */
  variant?: 'panel' | 'menu'
}

withDefaults(defineProps<Props>(), {
  dismissable: true,
  variant: 'panel',
})

const popoverRef = ref<InstanceType<typeof Popover> | null>(null)

// `target` anchors the panel to an element other than the one clicked — e.g. a
// right-aligned marker, so a panel lines up with its card's right edge.
const show = (event: Event, target?: HTMLElement | null) =>
  popoverRef.value?.show(event, target ?? undefined)
const hide = () => popoverRef.value?.hide()
const toggle = (event: Event, target?: HTMLElement | null) =>
  popoverRef.value?.toggle(event, target ?? undefined)

defineExpose({ show, hide, toggle })
</script>

<template>
  <Popover
    ref="popoverRef"
    :dismissable="dismissable"
    class="app-popover"
    :class="{ 'app-popover--menu': variant === 'menu' }"
  >
    <slot />
  </Popover>
</template>

<style>
/* Not scoped — targets the PrimeVue portal-rendered element */
.app-popover.p-popover {
  border-radius: 14px;
  border: 1px solid var(--p-primary-500);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 0;
  overflow: hidden;
  min-width: 220px;
  background: var(--p-surface-0);
}

.app-popover.p-popover .p-popover-content {
  padding: 0;
}

/* Compact action menu — neutral border, as wide as its longest item. */
.app-popover.app-popover--menu.p-popover {
  min-width: 0;
  border-radius: var(--p-border-radius-md);
  border-color: var(--p-surface-500);
  box-shadow: 0 4px 12px rgba(10, 13, 18, 0.08);
}

/* Hide default arrow */
.app-popover.p-popover-flipped .p-popover-arrow,
.app-popover.p-popover .p-popover-arrow {
  display: none;
}
</style>
